/**
 * ChildSwitcher — parent selects which child to view.
 * Fetches children list from /api/portal/children, stores selection in Zustand.
 * On mount: auto-selects first child if no selection saved.
 */
import { useEffect } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePortalChildren } from '@client/lib/queries/use-portal';
import { useChildSelector } from '@client/store/child-selector';

export function ChildSwitcher() {
  const { data: children, isLoading } = usePortalChildren();
  const { selectedChildId, setSelectedChildId } = useChildSelector();

  // Auto-select first child on mount or if current selection no longer valid
  useEffect(() => {
    if (!children || children.length === 0) return;
    const valid = children.some((c) => c.id === selectedChildId);
    if (!valid) {
      setSelectedChildId(children[0]?.id ?? null);
    }
  }, [children, selectedChildId, setSelectedChildId]);

  if (isLoading) {
    return (
      <div className="h-9 w-full rounded-md border border-border bg-muted animate-pulse" />
    );
  }

  if (!children || children.length === 0) {
    return (
      <div className="text-xs text-muted-foreground px-2 py-1 border border-border rounded-md truncate">
        Chưa có học viên liên kết
      </div>
    );
  }

  // Single child: show label without switcher UI
  if (children.length === 1) {
    const child = children[0]!;
    return (
      <div className="flex items-center gap-1 text-sm font-medium truncate px-2 py-1 border border-border rounded-md bg-muted/40">
        <span className="truncate">{child.fullName}</span>
        <span className="text-xs text-muted-foreground shrink-0">· {child.branchName}</span>
      </div>
    );
  }

  return (
    <Select
      value={selectedChildId ?? children[0]?.id ?? ''}
      onValueChange={(val) => setSelectedChildId(val)}
    >
      <SelectTrigger className="h-9 text-sm w-full">
        <SelectValue placeholder="Chọn học viên" />
      </SelectTrigger>
      <SelectContent>
        {children.map((child) => (
          <SelectItem key={child.id} value={child.id}>
            <span className="font-medium">{child.fullName}</span>
            <span className="ml-1.5 text-xs text-muted-foreground">· {child.branchName}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
